import React, { Component } from 'react'
import { Descriptions, Form, FormInstance, Select, Typography } from 'antd'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import { ShelterConsumer, ShelterContextData } from 'context/ShelterContext'
import { PartnerKeyType } from 'interfaces/IIntegration'
import { sbListLocations } from 'services/integration'
import ShelterBuddyExternalField from '../ShelterBuddyExternalField'
import MappingStepper, { Step } from '../MappingStepper'
import { DIRECTION_LABEL, DirectionKey, Flow, Partner, TypeKey } from '../shared/types'
import { resolveDirectionKey, resolveFlow, resolveTypeKey } from '../shared/resolvers'
import { ensureSbOk, mapSbOptions } from '../shared/sbOptions'
import { ActiveField, IncomingStep, IntegrationField, ManualExternalInputs, PartnerField, PriorityField } from '../shared/fields'
import { DirectionField, ExpressionField, TypeStep } from '../shared/match'

/** Loads the Shelter Buddy location list for an integration. (Nested `data.Data` payload.) */
const loadLocations = async (integrationUuid: string) => {
  const response = await sbListLocations({ integration_uuid: integrationUuid })
  ensureSbOk(response, 'locations')
  return mapSbOptions(response.data.data?.Data)
}

/**
 * Mapping types Branch currently supports.
 */
const ALLOWED_TYPES: TypeKey[] = ['DIRECT', 'MATCH']

/** Resolve the initial type from the record, clamped to what Branch allows today. */
const resolveBranchTypeKey = (form: FormInstance): TypeKey => {
  const resolved = resolveTypeKey(form)
  return ALLOWED_TYPES.includes(resolved) ? resolved : ALLOWED_TYPES[0]
}

type MappingBranchFormProps = {
  form: FormInstance
  partnerList: Partner[] | null
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingBranchFormInnerProps = MappingBranchFormProps & {
  shelterContext: ShelterContextData
}

type MappingBranchFormInnerState = {
  typeKey: TypeKey
  flow: Flow
  directionKey: DirectionKey
}

class MappingBranchFormInner extends Component<MappingBranchFormInnerProps, MappingBranchFormInnerState> {
  constructor(props: MappingBranchFormInnerProps) {
    super(props)
    this.state = {
      typeKey: resolveBranchTypeKey(props.form),
      flow: resolveFlow(props.form, 'branch_uuid'),
      directionKey: resolveDirectionKey(props.form),
    }
  }

  componentDidMount() {
    const { shelterContext, form } = this.props

    // Initial values may settle after the Form mounts — re-resolve everything.
    const typeKey = resolveBranchTypeKey(form)
    const flow = resolveFlow(form, 'branch_uuid')
    const directionKey = resolveDirectionKey(form)

    // Persist the resolved enum defaults so a never-touched new mapping still
    // submits them (the radios below are state-driven, not Form-bound directly).
    form.setFieldsValue({ type_key: typeKey })
    if (typeKey === 'MATCH') form.setFieldsValue({ direction_key: directionKey })
    if (form.getFieldValue('priority') == null) form.setFieldsValue({ priority: 0 })

    if (
      typeKey !== this.state.typeKey ||
      flow !== this.state.flow ||
      directionKey !== this.state.directionKey
    ) {
      this.setState({ typeKey, flow, directionKey })
    }

    if (!shelterContext.fetching && !shelterContext.shelters?.length) {
      shelterContext.requestShelterList({
        options: {
          limit: 9999,
          sort: ['name'],
        },
        search: {
          _fields: ["id", "uuid", "name"],
          organization_branches: {
            with: {
              _fields: ["uuid", "name", "shelter_id"],
              active: true,
              deleted: false,
            }
          },
          organization_integrations: {
            with: {
              _fields: ["id", "uuid", "description", "partner_key", "main", "shelter_id"],
              active: true,
              deleted: false,
            }
          }
        }
      })
    }
  }

  /** Resolve a shelter name for display, from the loaded shelter list. */
  shelterName = (uuid?: string) =>
    this.props.shelterContext.shelters?.find(s => s.uuid === uuid)?.name

  /** Resolve a branch name for display, searching every loaded shelter's branches. */
  branchName = (uuid?: string) => {
    for (const shelter of this.props.shelterContext.shelters ?? []) {
      const branch = shelter.organization_branches?.find(b => b.uuid === uuid)
      if (branch) return branch.name
    }
    return undefined
  }

  /** Resolve a partner display name. */
  partnerName = (key?: string) =>
    this.props.partnerList?.find(p => p.key === key)?.name || key

  // ----- Field groups -----

  /**
   * Type carrier: the DIRECT/MATCH picker when more than one type is allowed,
   * otherwise just the hidden enum fields that keep `type_key`/`direction_key`
   * in the payload.
   */
  renderType = () => (
    <TypeStep
      form={this.props.form}
      value={this.state.typeKey}
      directionKey={this.state.directionKey}
      onChange={typeKey => this.setState({ typeKey })}
      allowedTypes={ALLOWED_TYPES}
    />
  )

  /** Shelter is always required — branches belong to a shelter. */
  renderShelterField = () => {
    const { shelterContext } = this.props
    const shelterOptions = shelterContext.shelters?.map(shelter => ({
      label: shelter.name,
      value: shelter.uuid,
    }))

    return (
      <Form.Item
        label="Shelter"
        name="shelter_uuid"
        rules={[{ required: true, message: 'Missing a shelter' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          loading={shelterContext.fetching}
          placeholder="Select a shelter"
          options={shelterOptions}
        />
      </Form.Item>
    )
  }

  /** Adopets branch target (scoped to the selected shelter) — DIRECT and MATCH/INPUT. */
  renderBranchField = () => {
    const { shelterContext } = this.props

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) => prev.shelter_uuid !== cur.shelter_uuid}
      >
        {({ getFieldValue }) => {
          const shelterUuid = getFieldValue("shelter_uuid")
          const branchOptions = shelterContext.shelters
            ?.find(v => v.uuid === shelterUuid)
            ?.organization_branches
            ?.map(v => ({ label: v.name, value: v.uuid })) || []

          return (
            <Form.Item
              label="Location"
              name="branch_uuid"
            >
              <Select
                allowClear
                showSearch
                filterOption={DEFAULT_SELECT_FILTER_OPTION}
                loading={shelterContext.fetching}
                placeholder="Select a location (optional)"
                options={branchOptions}
              />
            </Form.Item>
          )
        }}
      </Form.Item>
    )
  }

  /** External (partner) target — Shelter Buddy locations picker, or name + id inputs. */
  renderExternalTarget = () => {
    const { form } = this.props

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.shelter_uuid !== cur.shelter_uuid ||
          prev.partner_key !== cur.partner_key ||
          prev.integration_uuid !== cur.integration_uuid
        }
      >
        {({ getFieldValue }) => {
          const shelterUuid: string | undefined = getFieldValue("shelter_uuid")
          const partnerKey: PartnerKeyType | undefined = getFieldValue("partner_key")
          const integrationUuid: string | undefined = getFieldValue("integration_uuid")

          const canLoadExternal =
            !!shelterUuid && !!integrationUuid && partnerKey === "SHELTER_BUDDY"

          if (canLoadExternal) {
            return (
              <ShelterBuddyExternalField
                form={form}
                integrationUuid={integrationUuid!}
                loadExternalList={loadLocations}
                label="External location"
                placeholder="Select a location"
              />
            )
          }

          return <ManualExternalInputs />
        }}
      </Form.Item>
    )
  }

  /** DIRECT/inbound only: read-only display of the incoming external value. */
  renderIncoming = () => (
    <IncomingStep
      form={this.props.form}
      partnerList={this.props.partnerList}
      externalRowLabel="External Location"
    />
  )

  // ----- Review (all variants) -----

  renderReview = () => {
    const { form } = this.props
    const { typeKey, directionKey } = this.state

    const partnerKey = form.getFieldValue('partner_key')
    const shelterUuid = form.getFieldValue('shelter_uuid')
    const branchUuid = form.getFieldValue('branch_uuid')
    const name = form.getFieldValue('name')
    const externalId = form.getFieldValue('external_id')
    const expression = form.getFieldValue('expression')

    const branchSide = this.branchName(branchUuid) || '—'
    const externalSide = [name, externalId].filter(Boolean).join(' · ') || '—'

    let directionLabel: string
    let mappingSummary: string
    if (typeKey === 'DIRECT') {
      // DIRECT mappings sync both ways — the internal direction is BOTH.
      directionLabel = DIRECTION_LABEL.BOTH
      mappingSummary = `${branchSide} ↔ ${externalSide}`
    } else {
      directionLabel = DIRECTION_LABEL[directionKey]
      mappingSummary = directionKey === 'INPUT'
        ? `expression → ${branchSide}`
        : `expression → ${externalSide}`
    }

    return (
      <>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Type">{typeKey}</Descriptions.Item>
          <Descriptions.Item label="Direction">{directionLabel}</Descriptions.Item>
          <Descriptions.Item label="Partner">{this.partnerName(partnerKey) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mapping">{mappingSummary}</Descriptions.Item>
          {typeKey === 'MATCH' && (
            <Descriptions.Item label="Expression">
              <Typography.Text code>{expression || '—'}</Typography.Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Shelter">
            {this.shelterName(shelterUuid) || shelterUuid || '—'}
          </Descriptions.Item>
        </Descriptions>

        <PriorityField />
        <ActiveField />
      </>
    )
  }

  // ----- Step definitions -----

  /**
   * The Type step appears only when more than one type is allowed; otherwise the
   * type is fixed and its hidden carry-fields ride along in the first real step.
   * The rest depends on type (and, for DIRECT, the flow).
   */
  steps = (): Step[] => {
    const showTypeStep = ALLOWED_TYPES.length > 1

    const typeStep: Step = {
      key: 'type',
      title: 'Type',
      fields: [],
      content: this.renderType,
    }

    const reviewStep: Step = {
      key: 'review',
      title: 'Review & Save',
      fields: ['priority'],
      content: this.renderReview,
    }

    if (this.state.typeKey === 'MATCH') {
      const ruleFields = this.state.directionKey === 'INPUT'
        ? ['expression', 'branch_uuid']
        : ['expression', 'name', 'external_id']

      return [
        ...(showTypeStep ? [typeStep] : []),
        {
          key: 'setup',
          title: 'Setup',
          fields: ['partner_key', 'shelter_uuid'],
          content: () => (
            <>
              {!showTypeStep && this.renderType()}
              <DirectionField
                form={this.props.form}
                value={this.state.directionKey}
                onChange={directionKey => this.setState({ directionKey })}
              />
              <PartnerField partnerList={this.props.partnerList} />
              {this.renderShelterField()}
            </>
          ),
        },
        {
          key: 'rule',
          title: 'Rule',
          fields: ruleFields,
          content: () => (
            <>
              <ExpressionField directionKey={this.state.directionKey} />
              {this.state.directionKey === 'INPUT'
                ? this.renderBranchField()
                : (<><IntegrationField shelterContext={this.props.shelterContext} />{this.renderExternalTarget()}</>)}
            </>
          ),
        },
        reviewStep,
      ]
    }

    // DIRECT — mirror the species form.
    if (this.state.flow === 'inbound') {
      return [
        typeStep,
        {
          key: 'incoming',
          title: 'Incoming',
          fields: [],
          content: this.renderIncoming,
        },
        {
          key: 'our-side',
          title: 'Our side',
          fields: ['shelter_uuid', 'branch_uuid'],
          content: () => (
            <>
              {this.renderShelterField()}
              {this.renderBranchField()}
            </>
          ),
        },
        reviewStep,
      ]
    }

    return [
      typeStep,
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['partner_key', 'shelter_uuid', 'branch_uuid'],
        content: () => (
          <>
            <PartnerField partnerList={this.props.partnerList} />
            {this.renderShelterField()}
            {this.renderBranchField()}
          </>
        ),
      },
      {
        key: 'external',
        title: 'External target',
        fields: ['name', 'external_id'],
        content: () => (
          <>
            <IntegrationField shelterContext={this.props.shelterContext} />
            {this.renderExternalTarget()}
          </>
        ),
      },
      reviewStep,
    ]
  }

  render() {
    return <MappingStepper form={this.props.form} steps={this.steps()} loading={this.props.loading} />
  }
}

export default class MappingBranchForm extends Component<MappingBranchFormProps> {
  render() {
    return (
      <ShelterConsumer>
        {(shelterContext) =>
          <MappingBranchFormInner
            form={this.props.form}
            partnerList={this.props.partnerList}
            loading={this.props.loading}
            shelterContext={shelterContext}
          />
        }
      </ShelterConsumer>
    )
  }
}
